import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Database } from "lucide-react";
import toast from "react-hot-toast";
import {
  getProspectLists,
  getViewConfig,
  createProspectList,
  createProspect,
  addProspectNote,
  addAppraisalToDatabase,
} from "../../../api/database";
import {
  categoryOptionValue,
  emptyCategoryTitles,
  isCategoryOption,
  resolveListChoice,
} from "../../database/emptyCategories";

// The Database's two top-level categories (ProspectList.VIEW_CHOICES /
// ProspectRecord.RECORD_TYPE_CHOICES). A seller lead is normally an owner, but
// plenty of "seller" enquiries turn out to be someone looking to buy, so both
// sides of the Database are offered.
const VIEWS = [
  { value: "owners", label: "Owners", recordType: "owner" },
  { value: "buyers", label: "Buyers", recordType: "buyer" },
];

const NEW_LIST = "__new__";
// Filing it nowhere is still allowed, but it has to be PICKED now. It used
// to be the blank default, so a rushed agent dropped the lead into no
// category at all and it was then only findable under "All" (Christian
// Gravias, Monday 13065049862, 2026-09-17: "Agents are adding to database
// from dashboard but they should have a choice where to put the leads").
const NO_CATEGORY = "__none__";

const AU_STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"];
const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

// Pull the suburb out of "60 Kernan Street, Strathmore VIC 3041" so the row lands
// on the Database's suburb filter and map, the same as the Add Record form does.
const suburbFromAddress = (address) => {
  if (!address) return "";
  const parts = String(address).split(",").map((s) => s.trim()).filter(Boolean);
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  const tokens = [];
  last.split(/\s+/).forEach((t) => {
    if (/^\d{4}$/.test(t)) return;
    if (AU_STATES.includes(t.toUpperCase())) return;
    tokens.push(t);
  });
  return titleCase(tokens.join(" ")).trim();
};

/**
 * "Add to Database" — asks which part of the Database the seller lead should be
 * filed into before the record is created: Owners or Buyers, and which category
 * (the list/tab) within it, with the option to start a new category on the spot
 * (Christian Gravias, Monday 12817261831, 2026-08-19). The button used to drop
 * every lead straight into the Owners grid with no category, so the agent had to
 * hunt the row down afterwards and move it.
 *
 * Creating the record mirrors it into a CRM Contact server-side, so this is the
 * only call needed. Marking the lead as actioned stays with the caller.
 *
 * SECOND MODE: pass `property` instead of `lead` and the same picker files an
 * APPRAISAL back into the Database. "I can add a property as an appraisal but
 * you cannot add your appraisal back to the database. Please a selection or
 * button to move a property of an appraisal back to our database and select the
 * correct tab" (Paul Jimoulis, CRM-2026-00070, Monday 13057749471). That path
 * posts the property id and the chosen tab to the server, which reads the vendor
 * off the property, so the pipeline card does not need to carry owner details it
 * is not served. An appraisal that came out of the Database goes back to the row
 * it came from rather than becoming a second copy of the same owner.
 */
const AddToDatabaseModal = ({ isOpen, lead, property, onClose, onSaved, defaultView = "owners", noteLabel }) => {
  const [view, setView] = useState(defaultView);
  const [lists, setLists] = useState([]);
  // Categories made on the Database page with nothing in them yet. They live
  // on the view config, not as lists, so they have to be fetched separately
  // or they never show here (CRM-2026-00099).
  const [emptyGroups, setEmptyGroups] = useState([]);
  const [listId, setListId] = useState("");
  const [newName, setNewName] = useState("");
  const [newGroup, setNewGroup] = useState("");
  const [loadingLists, setLoadingLists] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadLists = useCallback((v) => {
    setLoadingLists(true);
    Promise.all([
      getProspectLists(v).catch(() => ({ data: [] })),
      getViewConfig(v).catch(() => ({ data: {} })),
    ])
      .then(([listRes, cfgRes]) => {
        setLists(listRes?.data || []);
        setEmptyGroups(cfgRes?.data?.empty_groups || []);
      })
      .finally(() => setLoadingLists(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // Buyer Enquiries opens this with defaultView="buyers", because an enquiry
    // about a listing is a buyer, not an owner.
    setView(defaultView);
    setNewName("");
    setNewGroup("");
  }, [isOpen, lead?.id, property?.id, defaultView]);

  useEffect(() => {
    if (!isOpen) return;
    // Categories belong to one view, so switching Owners/Buyers reloads them and
    // drops whatever was picked on the other side.
    setListId("");
    loadLists(view);
  }, [isOpen, view, loadLists]);

  // Categories grouped under their heading, normally the suburb, the same way the
  // Database page shows its tabs. Ungrouped categories sit on their own.
  const { grouped, loose } = useMemo(() => {
    const byTitle = new Map();
    const ungrouped = [];
    lists.forEach((l) => {
      const title = (l.group_name || "").trim();
      if (!title) {
        ungrouped.push(l);
        return;
      }
      if (!byTitle.has(title)) byTitle.set(title, []);
      byTitle.get(title).push(l);
    });
    return {
      grouped: [...byTitle.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      loose: ungrouped,
    };
  }, [lists]);

  const emptyCategories = useMemo(() => emptyCategoryTitles(lists, emptyGroups), [lists, emptyGroups]);

  const subject = lead || property;
  if (!isOpen || !subject) return null;

  const address = property
    ? property.address ||
      [property.street_name, property.suburb, property.state].filter(Boolean).join(", ") ||
      property.title ||
      ""
    : lead.property_address || lead.owner_subject_address || "";
  const leadName = property ? property.owner_name || "" : lead.name || lead.full_name || "";
  const recordType = VIEWS.find((v) => v.value === view)?.recordType || "owner";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!listId) {
      toast.error("Choose where this goes in the Database.");
      return;
    }
    if (listId === NEW_LIST && !newName.trim()) {
      toast.error("Name the new category.");
      return;
    }
    setSaving(true);
    try {
      let targetList =
        listId && listId !== NEW_LIST && listId !== NO_CATEGORY ? listId : null;
      if (isCategoryOption(listId)) targetList = await resolveListChoice(view, listId);
      if (listId === NEW_LIST) {
        const res = await createProspectList({
          view,
          name: newName.trim(),
          group_name: newGroup.trim(),
        });
        targetList = res?.data?.id || null;
      }

      // An appraisal is filed server-side: the pipeline card is not served the
      // vendor's name, number or email (the listing payload leaves owner fields
      // out), so the server reads them off the property instead.
      if (property) {
        const res = await addAppraisalToDatabase({
          property_id: property.id,
          view,
          list: targetList,
        });
        toast.success(
          res?.data?.restored
            ? "Moved back into your Database."
            : "Added to your Database."
        );
        onSaved && onSaved(property, res?.data);
        onClose();
        return;
      }

      const payload = {
        record_type: recordType,
        list: targetList,
        name: leadName,
        mobile: lead.phone || "",
        email: lead.email || "",
      };
      if (recordType === "owner") {
        payload.owner_profile = { address, suburb: suburbFromAddress(address) };
      }
      const created = await createProspect(payload);

      // A buyer row has no address column, so the property the lead enquired
      // about would be lost. Keep it as a note rather than dropping it.
      if (recordType === "buyer" && address && created?.data?.id) {
        try {
          await addProspectNote(created.data.id, `${noteLabel || "From Seller Leads"} - property: ${address}`);
        } catch (noteErr) {
          console.error("Failed to note the seller lead address:", noteErr);
        }
      }

      // Carry the notes the agent wrote while the lead was still a lead onto the
      // Database row. Without this the call history stays on the Lead and the
      // record the agent will actually work from starts blank, which is the same
      // "notes arent saving properly" complaint one step later (Christian
      // Gravias, 2026-08-21). Never blocks the save: the row already exists.
      const leadNotes = Array.isArray(lead.agent_notes) ? lead.agent_notes : [];
      if (leadNotes.length && created?.data?.id) {
        for (const note of leadNotes) {
          const stamp = note?.timestamp ? `[${note.timestamp}] ` : "";
          const author = note?.author ? `(${note.author}) ` : "";
          const body = (note?.content || "").trim();
          if (!body) continue;
          try {
            await addProspectNote(created.data.id, `${stamp}${author}${body}`);
          } catch (noteErr) {
            console.error("Failed to carry a lead note across:", noteErr);
          }
        }
      }

      toast.success("Added to your Database.");
      onSaved && onSaved(lead);
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      toast.error(
        data?.detail || data?.list?.[0] || data?.name?.[0] || "Failed to add to the Database."
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]";
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    // z-[130] because this can be opened from ON TOP of the Edit Appraisal modal
    // (z-[110]) as well as from a list page, and at z-50 it rendered behind it.
    <div className="fixed inset-0 z-[130] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto">
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441]">
            {property ? "Add Appraisal to Database" : "Add to Database"}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            {property ? (
              <>
                <div className="font-bold text-gray-900 text-sm">{address || "This appraisal"}</div>
                <div className="mt-0.5">
                  {leadName
                    ? `Vendor: ${leadName}`
                    : "The vendor saved on this appraisal comes across with it."}
                </div>
              </>
            ) : (
              <>
                <div className="font-bold text-gray-900 text-sm">{leadName || "No Name"}</div>
                <div className="mt-0.5">
                  {[lead.phone, lead.email].filter(Boolean).join(" · ") || "No contact details"}
                </div>
                {address && <div className="mt-0.5">{address}</div>}
              </>
            )}
          </div>

          <div>
            <span className={labelClass}>Which part of the Database</span>
            <div className="flex gap-2">
              {VIEWS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setView(v.value)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold border transition-colors ${
                    view === v.value
                      ? "bg-[#55C5D0] border-[#55C5D0] text-white"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="add-to-database-category">
              Category
            </label>
            <select
              id="add-to-database-category"
              className={inputClass}
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              disabled={loadingLists}
            >
              <option value="">
                {loadingLists ? "Loading categories..." : "Choose a category..."}
              </option>
              {loose.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
              {grouped.map(([title, groupLists]) => (
                <optgroup key={title} label={title}>
                  {groupLists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </optgroup>
              ))}
              {emptyCategories.map((t) => (
                <option key={`cat-${t}`} value={categoryOptionValue(t)}>
                  {t}
                </option>
              ))}
              <option value={NEW_LIST}>+ New category...</option>
              <option value={NO_CATEGORY}>No category (All)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              The tab the row sits under in your Database. A category with no
              heading of its own gets its own section along the top of the
              Database page. "No category" leaves the row in All only.
            </p>
          </div>

          {listId === NEW_LIST && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>New category name *</label>
                <input
                  className={inputClass}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Chaselist"
                />
              </div>
              <div>
                <label className={labelClass}>Group heading (optional)</label>
                <input
                  className={inputClass}
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  placeholder="Preston"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2"
          >
            <Database className="w-4 h-4" />
            {saving ? "Adding..." : "Add to Database"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddToDatabaseModal;
